import { Link } from "react-router-dom";
import { MapPin, Clock, Phone, Mail, Car, ArrowUpRight } from "lucide-react";
import useSEO from "../lib/useSEO";
import useReveal from "../lib/useReveal";
import SectionHeading from "../components/SectionHeading";
import { IMG } from "../lib/media";
import { ORDER_ONLINE_URL } from "../lib/links";

const HOURS = [
  ["Monday", "9:00 — 20:00"],
  ["Tuesday", "9:00 — 20:00"],
  ["Wednesday", "9:00 — 20:00"],
  ["Thursday", "9:00 — 21:00"],
  ["Friday", "9:00 — 21:00"],
  ["Saturday", "9:00 — 21:00"],
  ["Sunday", "Closed"],
];

export default function Visit() {
  const ref = useReveal();
  useSEO({
    title: "Visit Us — SugaSpice Bakery & Kitchen, St Neots",
    description: "Find SugaSpice Bakery & Kitchen in St Neots, Cambridgeshire. Opening hours, address, parking and contact details for our bakery and kitchen.",
    image: IMG.interior_main,
  });

  return (
    <div ref={ref}>
      <section className="pt-12 md:pt-16 pb-16 bg-[#F7F5F0]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7 fade-up">
              <span className="text-[11px] font-semibold tracking-[0.28em] uppercase text-[#C8102E]">Visit us</span>
              <h1 className="mt-5 font-display text-[clamp(2.8rem,7vw,6rem)] leading-[0.9] tracking-tight uppercase">
                Stop by. <br />
                <span className="text-[#C8102E]">For a bite.</span>
              </h1>
              <p className="mt-6 max-w-xl text-base md:text-lg text-[#3a3a3a] leading-relaxed">
                A modern bakery and kitchen takeaway in the heart of St Neots — fresh morning breakfast, fried chicken and burgers come afternoon/evening, sweet treats and savouries galore, and a coffee that earns its rent.
              </p>
              <p className="mt-4 max-w-xl text-base text-[#3a3a3a] leading-relaxed">
                We're right on Moores Walk in the middle of St Neots, an easy walk from the Market Square and the town's main car parks. Whether you're grabbing a warm breakfast bap on the way to work, picking up a box of fresh doughnuts and cakes for the office, or stopping in for a smash burger and loaded fries later in the day, there's always something coming out of the kitchen. Everything's freshly prepped and cooked to order, with the bread, pastries and cakes in and ready the moment we open.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-8 bg-[#F7F5F0]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 fade-up">
            {[
              { t: "Fresh from 9am", d: "Breakfast baps, toasted muffins, pastries and fresh coffee from opening. Beat the queue and start the day right." },
              { t: "Kitchen open all day", d: "Smash burgers, crispy fried chicken, wings, loaded fries and bold Indian street food cooked to order through to late." },
              { t: "Grab & go bakery", d: "Fresh bread, cakes, doughnuts, brownies and savouries to take away — perfect for the office, home or a treat on the move." },
            ].map((c) => (
              <div key={c.t} className="bg-white rounded-3xl border border-[#D1C9BE]/60 p-6">
                <h3 className="font-display text-xl uppercase tracking-tight">{c.t}</h3>
                <p className="mt-3 text-sm text-[#5C5C5C] leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24 bg-[#F7F5F0]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7 fade-up">
              <div className="aspect-[16/10] rounded-3xl overflow-hidden shadow-[0_30px_60px_rgba(0,0,0,0.16)]">
                <img loading="lazy" decoding="async" src={IMG.interior_main} alt="SugaSpice Bakery interior" className="w-full h-full object-cover" />
              </div>
              <div className="mt-6 aspect-[16/8] rounded-3xl overflow-hidden">
                <iframe
                  title="SugaSpice St Neots location"
                  src="https://www.google.com/maps?q=SugaSpice+Bakery+%26+Kitchen,+10+Moore%27s+Walk,+St+Neots+PE19+1AG,+UK&output=embed"
                  className="w-full h-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid="visit-map"
                />
              </div>
            </div>

            <div className="lg:col-span-5 fade-up">
              <div className="bg-[#1A1A1A] text-[#F7F5F0] rounded-3xl p-8 md:p-10">
                <h3 className="font-display text-3xl md:text-4xl uppercase tracking-tight">Find us</h3>
                <ul className="mt-8 space-y-6 text-sm">
                  <li className="flex gap-4">
                    <MapPin className="h-5 w-5 mt-1 text-[#C8102E]" />
                    <div>
                      <p className="font-semibold tracking-wide">SugaSpice Bakery & Kitchen</p>
                      <p className="text-[#C9C5BE]">10 Moores Walk<br />St Neots, PE19 1AG — UK</p>
                      <a
                        href="https://www.google.com/maps/dir/?api=1&destination=SugaSpice%20Bakery%20%26%20Kitchen%2C%2010%20Moore%27s%20Walk%2C%20St%20Neots%20PE19%201AG"
                        target="_blank" rel="noopener noreferrer"
                        data-testid="visit-directions"
                        className="inline-block mt-2 text-[#C8102E] hover:text-[#F0889B] font-semibold transition-colors"
                      >Get directions →</a>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <Phone className="h-5 w-5 mt-1 text-[#C8102E]" />
                    <a href="tel:+441480763192" className="text-[#C9C5BE] hover:text-[#F7F5F0] transition-colors" data-testid="visit-phone">01480 763192</a>
                  </li>
                  <li className="flex gap-4">
                    <Mail className="h-5 w-5 mt-1 text-[#C8102E]" />
                    <p className="text-[#C9C5BE]">sugaspicebakery01@gmail.com</p>
                  </li>
                  <li className="flex gap-4">
                    <Car className="h-5 w-5 mt-1 text-[#C8102E]" />
                    <p className="text-[#C9C5BE]">Free street parking close by. Collection point inside the door for online orders.</p>
                  </li>
                </ul>

                <div className="mt-10 pt-8 border-t border-white/10">
                  <h4 className="font-display text-xl uppercase tracking-tight flex items-center gap-2">
                    <Clock className="h-5 w-5 text-[#C8102E]" /> Opening hours
                  </h4>
                  <ul className="mt-5 space-y-2 text-sm">
                    {HOURS.map(([d, h]) => (
                      <li key={d} className="flex justify-between border-b border-white/5 pb-2">
                        <span>{d}</span>
                        <span className="text-[#C9C5BE]">{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <a href={ORDER_ONLINE_URL} target="_blank" rel="noopener noreferrer" className="btn-pill mt-10 w-full bg-[#C8102E] text-[#F7F5F0] hover:bg-[#A50D24]" data-testid="visit-order-cta">
                  Order Ahead <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
